import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Package, RefreshCw, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import adminService, { Category } from '../../services/admin/adminService';
import DataTable from '../../components/common/DataTable';

const CategoryManagement: React.FC = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; category: Category | null }>({
    show: false,
    category: null
  });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async (showToast = false) => {
    try {
      if (showToast) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const [allCategories, categoriesWithCount] = await Promise.all([
        adminService.getAllCategories(),
        adminService.getCategoriesWithBusinessCount()
      ]);

      const mergedCategories = allCategories
        .map(category => {
          const countData = categoriesWithCount.find(c => c.category_id === category.category_id);
          return {
            ...category,
            business_count: countData?.business_count || 0
          };
        })
        .sort((a, b) => a.category_name.localeCompare(b.category_name));

      setCategories(mergedCategories);

      if (showToast) {
        toast.success('Categories refreshed!');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch categories');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!deleteModal.category) return;

    try {
      setDeleting(true);
      await adminService.deleteCategory(deleteModal.category.category_id);
      toast.success('Category deleted successfully!');
      setDeleteModal({ show: false, category: null });
      fetchCategories();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete category');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="animate-spin text-primary-500" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-secondary-900">Category Management</h1>
          <p className="text-sm sm:text-base text-secondary-600 mt-1">Manage business categories with custom fields and groups</p>
        </div>
        <div className="flex flex-wrap sm:flex-nowrap gap-3 w-full sm:w-auto">
          <button
            onClick={() => fetchCategories(true)}
            disabled={refreshing}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          <button
            onClick={() => navigate('/admin/categories/add')}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2"
          >
            <Plus size={18} />
            Create
          </button>
        </div>
      </div>

      {/* Categories Table */}
      <DataTable
        data={categories}
        loading={loading}
        searchable={true}
        searchPlaceholder="Search categories by name or slug..."
        searchFields={['category_name', 'category_slug']}
        filterable={true}
        filterOptions={[
          { label: 'Active', value: 'active', field: 'is_active' },
          { label: 'Inactive', value: 'inactive', field: 'is_active' }
        ]}
        defaultPageSize={10}
        emptyMessage="No categories found"
        emptyIcon={<Package size={64} className="text-secondary-300 mx-auto mb-4" />}
        columns={[
          {
            header: 'Category',
            accessor: (category) => (
              <div>
                <h3 className="text-base font-semibold text-secondary-900">
                  {category.category_name}
                </h3>
                <p className="text-xs text-secondary-500">{category.category_slug}</p>
              </div>
            ),
            sortable: true,
          },
          {
            header: 'Description',
            accessor: (category) => (
              <p className="text-sm text-secondary-600 max-w-md">
                {category.category_description || <span className="text-gray-400">No description</span>}
              </p>
            ),
            className: 'hidden sm:table-cell',
          },
          {
            header: 'Businesses',
            accessor: (category) => (
              <span className="inline-flex items-center justify-center w-10 h-10 bg-primary-100 text-primary-700 rounded-lg text-sm font-bold">
                {category.business_count || 0}
              </span>
            ),
            align: 'center',
            sortable: true,
          },
          {
            header: 'Status',
            accessor: (category) => (
              <span
                className={`inline-block px-3 py-1 text-xs rounded-full font-semibold ${category.is_active === 1
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
                  }`}
              >
                {category.is_active === 1 ? 'Active' : 'Inactive'}
              </span>
            ),
            align: 'center',
            sortable: true,
          },
        ]}
        renderActions={(category) => (
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/admin/categories/edit/${category.category_id}`);
              }}
              className="bg-primary-50 hover:bg-primary-100 text-primary-600 p-2 rounded-lg transition-colors"
              title="Edit Category"
            >
              <Edit2 size={18} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setDeleteModal({ show: true, category });
              }}
              className="bg-red-50 hover:bg-red-100 text-red-600 p-2 rounded-lg transition-colors"
              title="Delete Category"
            >
              <Trash2 size={18} />
            </button>
          </div>
        )}
      />

      {/* Delete Confirmation Modal */}
      {deleteModal.show && deleteModal.category && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertCircle size={24} className="text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-secondary-900">Delete Category</h3>
              </div>

              <p className="text-secondary-600 mb-6">
                Are you sure you want to delete{' '}
                <strong>{deleteModal.category.category_name}</strong>? This action cannot be
                undone.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={handleDeleteCategory}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  onClick={() => setDeleteModal({ show: false, category: null })}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-gray-200 text-secondary-900 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryManagement;
